import { HttpStatusCode, isAxiosError } from "axios";
import type { TFunction } from "i18next";
import { z } from "zod";

export type CalculationFailure =
    | { kind: "property_multiple_headings"; property: string; ssqTitle: string }
    | { kind: "invalid_dimension_formula"; property: string; ssqTitle: string }
    | { kind: "ssq_configuration"; ssqTitle: string }
    | { kind: "outage" }
    | { kind: "unknown" };

const SsqConfigurationErrorSchema = z.object({
    errorType: z.string(),
    ssqTitle: z.string(),
    propertyTitle: z.string().nullish(),
});

export const classifyCalculationFailure = (error: unknown): CalculationFailure => {
    if (!isAxiosError(error) || !error.response) {
        return { kind: "unknown" };
    }

    const { status, data } = error.response;

    if (status === HttpStatusCode.ServiceUnavailable) {
        return { kind: "outage" };
    }

    const configurationError = SsqConfigurationErrorSchema.safeParse(data);
    if (status !== HttpStatusCode.UnprocessableEntity || !configurationError.success) {
        return { kind: "unknown" };
    }

    const { errorType, ssqTitle, propertyTitle } = configurationError.data;

    if (errorType === "property_multiple_headings" && propertyTitle) {
        return { kind: "property_multiple_headings", property: propertyTitle, ssqTitle };
    }

    if (errorType === "property_formula_invalid_dimension_formula" && propertyTitle) {
        return { kind: "invalid_dimension_formula", property: propertyTitle, ssqTitle };
    }

    return { kind: "ssq_configuration", ssqTitle };
};

export const calculationFailureMessage = (failure: CalculationFailure, t: TFunction): string => {
    switch (failure.kind) {
        case "property_multiple_headings":
            return t(
                "calculation.error.property_multiple_headings",
                "“{{property}}” is assigned to several headings in the questionnaire “{{ssq}}”. Contact your administrator.",
                { property: failure.property, ssq: failure.ssqTitle },
            );
        case "invalid_dimension_formula":
            return t(
                "calculation.error.invalid_dimension_formula",
                "The formula of “{{property}}” in the questionnaire “{{ssq}}” is invalid. Contact your administrator.",
                { property: failure.property, ssq: failure.ssqTitle },
            );
        case "ssq_configuration":
            return t(
                "calculation.error.ssq_configuration",
                "The questionnaire “{{ssq}}” is misconfigured, so its scores cannot be calculated. Contact your administrator.",
                { ssq: failure.ssqTitle },
            );
        case "outage":
            return t(
                "calculation.error.service_unavailable",
                "The calculation service is temporarily unavailable. Try again in a moment.",
            );
        case "unknown":
            return t("calculation.unexpectederror", "Unexpected error during calculation");
    }
};
